// src/pages/Notices.tsx
import React from "react";
import Header from "components/Header";
import Footer from "components/Footer";
import { getJSON } from "lib/api";

type Notice = {
  id: string;
  title: string;
  body?: string;
  level?: "info" | "warning" | "urgent";
  linkUrl?: string;
  pinned?: boolean;

  // ★ startAt を優先表示（開始日時）
  startAt?: string;

  // ★ 保険（startAt が無いデータ用）
  createdAt?: string;
};

const PER_PAGE = 10;

/** ISO/日付文字列 → 2025年12月6日(土) もしくは 2025年12月6日(土) 15:00 形式に整形 */
const formatJPDateTime = (value?: string): string => {
  if (!value) return "";

  const raw = String(value).trim();
  if (!raw) return "";

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";

  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const w = ["日", "月", "火", "水", "木", "金", "土"][d.getDay()];

  // 日付だけ入力（YYYY-MM-DD）なら時刻は出さない
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw);

  if (isDateOnly) {
    return `${y}年${m}月${day}日(${w})`;
  }

  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}年${m}月${day}日(${w}) ${hh}:${mm}`;
};

export default function NoticesPage() {
  const [rows, setRows] = React.useState<Notice[]>([]);
  const [err, setErr] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);

  // ★ 「もっと見る」押下で全文表示する対象（notice.id）
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    (async () => {
      try {
        setErr("");
        setLoading(true);
        const data = await getJSON<Notice[]>("/api/public/notices");
        setRows(Array.isArray(data) ? data : []);
        setPage(1);
        // 取得し直し時は開閉状態をリセット（混乱防止）
        setExpandedIds(new Set());
      } catch (e: any) {
        setErr(e?.message || "お知らせの取得に失敗しました。");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // rows 変更時にページ番号を補正
  React.useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
  const startIndex = (page - 1) * PER_PAGE;
  const pagedRows = rows.slice(startIndex, startIndex + PER_PAGE);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <main style={{ maxWidth: 760, margin: "0 auto" }}>
      <Header />

      <section style={{ padding: "16px 12px" }}>
        <h1 style={{ margin: "0 0 12px" }}>お知らせ</h1>

        {loading && <div>読み込み中…</div>}

        {!loading && err && (
          <div style={{ color: "#d32f2f" }}>
            お知らせの取得に失敗しました。{err}
          </div>
        )}

        {!loading && !err && rows.length === 0 && (
          <div style={{ color: "#666" }}>現在お知らせはありません。</div>
        )}

        {!loading && !err && rows.length > 0 && (
          <>
            <div style={{ display: "grid", gap: 10 }}>
              {pagedRows.map((n) => {
                // ★ 表示日付は startAt 優先。無い場合のみ createdAt。
                const dateStr = formatJPDateTime(n.startAt || n.createdAt);

                const linesAll: string[] =
                  (n.body || "")
                    .split(/\r?\n/)
                    .map((s) => s.trim())
                    .filter(Boolean) || [];

                const isExpanded = expandedIds.has(String(n.id));
                const linesToShow = isExpanded ? linesAll : linesAll.slice(0, 3);
                const hasMore = linesAll.length > 3;

                const bg =
                  n.level === "urgent"
                    ? "#ffebee"
                    : n.level === "warning"
                    ? "#fff8e1"
                    : "#ffffff";
                const border =
                  n.level === "urgent"
                    ? "1px solid #e53935"
                    : n.level === "warning"
                    ? "1px solid #f9a825"
                    : "1px solid #eee";

                return (
                  <article
                    key={n.id}
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      background: bg,
                      border,
                    }}
                  >
                    {/* 見出し：📣 おしらせ */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontWeight: 700,
                        marginBottom: 4,
                      }}
                    >
                      <span role="img" aria-label="megaphone">
                        📣
                      </span>
                      <span>おしらせ</span>
                      {n.pinned && (
                        <span
                          style={{
                            marginLeft: 4,
                            fontSize: 11,
                            padding: "1px 6px",
                            borderRadius: 999,
                            background: "#ffeb3b",
                          }}
                        >
                          PIN
                        </span>
                      )}
                    </div>

                    {/* 日付（startAt 優先 + 時刻も表示） */}
                    {dateStr && (
                      <div
                        style={{
                          fontSize: 12,
                          opacity: 0.8,
                          marginBottom: 4,
                        }}
                      >
                        {dateStr}
                      </div>
                    )}

                    {/* タイトル */}
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        marginBottom: 4,
                      }}
                    >
                      {n.title}
                    </div>

                    {/* 本文（初期3行 → ボタンで全文） */}
                    {linesAll.length > 0 ? (
                      <div
                        style={{
                          fontSize: 13,
                          lineHeight: 1.5,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {linesToShow.map((line, idx) => (
                          <div key={idx}>{line}</div>
                        ))}

                        {hasMore && (
                          <div style={{ marginTop: 8 }}>
                            <button
                              type="button"
                              onClick={() => toggleExpand(String(n.id))}
                              style={{
                                padding: "6px 10px",
                                borderRadius: 8,
                                border: "1px solid #cbd5e1",
                                background: "#fff",
                                fontWeight: 700,
                                fontSize: 12,
                                cursor: "pointer",
                              }}
                            >
                              {isExpanded ? "閉じる" : "もっと見る"}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: "#666" }}>
                        お知らせの本文が登録されていません。
                      </div>
                    )}

                    {/* リンクURL（任意） */}
                    {n.linkUrl && (
                      <p style={{ margin: "6px 0 0" }}>
                        <a
                          href={n.linkUrl}
                          style={{
                            color: "#1976d2",
                            textDecoration: "underline",
                          }}
                        >
                          詳細を見る →
                        </a>
                      </p>
                    )}
                  </article>
                );
              })}
            </div>

            {/* ページネーション */}
            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 12,
                fontSize: 13,
              }}
            >
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{
                  padding: "6px 12px",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  background: page <= 1 ? "#f5f5f5" : "#fff",
                  cursor: page <= 1 ? "default" : "pointer",
                }}
              >
                前へ
              </button>
              <span>
                {page} / {totalPages} ページ（全 {rows.length} 件）
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{
                  padding: "6px 12px",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  background: page >= totalPages ? "#f5f5f5" : "#fff",
                  cursor: page >= totalPages ? "default" : "pointer",
                }}
              >
                次へ
              </button>
            </div>
          </>
        )}
      </section>

      <Footer />
    </main>
  );
}
